/**
 * Field Helper Utilities
 *
 * Purpose: Utility functions for field data transformation and validation
 *
 * Functions:
 * - getFieldLabel: Get display label for a field
 * - getFieldScopeInfo: Determine scope information for a field
 * - canRevertField: Check if a field can be reverted to global
 * - prepareGlobalFieldMock: Create mock global field for revert dialog
 *
 * Author: Development Team
 * Created: 2025-01-21 (Refactoring Phase 3)
 */

import { Field, FieldDefinition } from '../services/metadataService';

/**
 * Get the display label for a field
 * Uses label_override if available, otherwise field_definition_label
 */
export function getFieldLabel(field: Field): string {
  return field.label_override || field.field_definition_label || '';
}

/**
 * Get scope information for a field
 */
export interface FieldScopeInfo {
  scope: string;
  isOverride: boolean;
  hasGlobalParent: boolean;
}

export function getFieldScopeInfo(field: Field): FieldScopeInfo {
  // Use scope_info from API response if available
  if (field.scope_info) {
    return {
      scope: field.scope_info.scope === 'Overridden' ? 'OVERRIDDEN' : field.scope_info.scope,
      isOverride: field.scope_info.is_override,
      hasGlobalParent: field.scope_info.has_global_parent,
    };
  }

  // Fallback to tenant_id logic
  return {
    scope: field.tenant_id === 'GLOBAL' ? 'GLOBAL' : 'LOCAL',
    isOverride: false,
    hasGlobalParent: false,
  };
}

/**
 * Check if a field can be reverted to global
 * A field can be reverted if it's an override (scope is 'Overridden' or 'OVERRIDDEN')
 */
export function canRevertField(field: Field): boolean {
  const scopeInfo = getFieldScopeInfo(field);
  return scopeInfo.scope === 'OVERRIDDEN' || scopeInfo.scope === 'Overridden';
}

/**
 * Create a mock global field object from field definition for revert dialog display
 */
export function prepareGlobalFieldMock(
  originalField: Field,
  globalFieldDefinition: FieldDefinition
): Field {
  return {
    field_id: `global-${originalField.field_id}`,
    fielddef_id: originalField.fielddef_id,
    tenant_id: 'GLOBAL',
    model_id: originalField.model_id,
    model_name: originalField.model_name,
    model_type: originalField.model_type,
    storage_type: originalField.storage_type,
    label_override: globalFieldDefinition.label,
    field_definition_label: globalFieldDefinition.label,
    is_required: false,
    is_multiselect: globalFieldDefinition.is_multiselect,
    status_override: globalFieldDefinition.status,
    created_at: globalFieldDefinition.created_at,
    updated_at: globalFieldDefinition.updated_at,
    created_by: globalFieldDefinition.created_by,
    updated_by: globalFieldDefinition.updated_by,
    scope_info: {
      scope: 'GLOBAL',
      is_override: false,
      has_global_parent: false,
    },
  };
}

/**
 * Get the status value for a field (handles null/undefined status_override)
 */
export function getFieldStatus(field: Field): 'active' | 'disabled' {
  return !field.status_override || field.status_override === 'active' ? 'active' : 'disabled';
}

/**
 * Check if user can edit a field based on parent field definition access
 */
export function getEffectiveCanEdit(
  baseCanEdit: boolean,
  fieldDefinition: FieldDefinition | null,
  isGlobalTenant: boolean
): boolean {
  // If no field definition or user is GLOBAL, use base permission
  if (!fieldDefinition || isGlobalTenant) {
    return baseCanEdit;
  }

  // Check access level for non-GLOBAL users
  const accessLevel = fieldDefinition.access;
  if (accessLevel === '0') {
    // Internal Only - cannot edit
    return false;
  } else if (accessLevel === '1') {
    // Client Read-Only - cannot edit
    return false;
  } else {
    // Client Overrideable (2) or Client Overrideable All (3)
    return baseCanEdit;
  }
}

/**
 * Check if user can view related fields based on parent field definition access
 */
export function canViewRelatedFields(
  fieldDefinition: FieldDefinition | null,
  isGlobalTenant: boolean
): boolean {
  // If no field definition or user is GLOBAL, can view
  if (!fieldDefinition || isGlobalTenant) {
    return true;
  }

  // Check access level for non-GLOBAL users
  const accessLevel = fieldDefinition.access;
  if (accessLevel === '0') {
    // Internal Only - cannot view
    return false;
  }

  // All other access levels can view
  return true;
}
